import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { createSelector } from 'reselect';
import moment from 'moment';
import { Action } from 'redux';

import {
  prepareReceiptData,
  prepareCouponData,
  prepareGiftCardData,
} from 'actions/integrations/printer/preparePrintoutData';
import {
  getSalesDocuments,
  getIssuedCoupons,
  getCoupons,
  getGiftCard,
} from 'services/ErplyAPI';
import { requestCompleteResponse } from 'services/ErplyAPI/core/ErplyAPI';
import { getProductsUniversal } from 'actions/productsDB';
import { getDefaultReceiptTemplateFor } from 'reducers/patchScript_salesReceiptTemplate';

import { INTEGRATION_TYPES, INTEGRATIONS } from '../constants/CAFA';
import {
  PatchScriptTemplate,
  TItem,
} from '../containers/Forms/Settings/views/Debug/components/ReceiptSchema/Editor/schema/comms';

import { getCafaEntry, getCafaEntry2 } from './cafaConfigs';
import {
  getShouldShowCardDataOnReceipt,
  getShouldShowCashChangeOnReceipt,
  getShouldShowPrintAttemptCount,
  getShouldShowProductCode,
  getShowPricesWithTax,
  getShowTotalDiscountOnReceipt,
} from './configs/settings';
import { getCompany, getLoggedInEmployeeID } from './Login';
import { getSelectedWarehouseID, getSelectedWarehouse } from './warehouses';
import { getEmployees } from './cachedItems/employees';

const conditionalShowSingleItem = <T>(show: boolean, value: T): [T] | [] => {
  if (show) return [value];
  return [];
};

// prettier-ignore
const defaults: Partial<{ [key in PrintoutType | 'defaultType']: { schema: (state) => PatchScriptTemplate, prepareData: ThunkAction<any, any, any, any> } }> = {
  salesReceipt: {
    schema: getDefaultReceiptTemplateFor.salesReceipt,
    prepareData: async (dispatch, getState) => {
      const [{ id }] = await getSalesDocuments({
        recordsPerPage: 1,
      })
      return dispatch(prepareReceiptData({ salesDocument: { id }, type: "salesReceipt" }));
    }
  },
  giftReceipt: {
    schema: getDefaultReceiptTemplateFor.giftReceipt,
    prepareData: async (dispatch, getState) => {
      const [{ id }] = await getSalesDocuments({
        recordsPerPage: 1,
      })
      return dispatch(prepareReceiptData({ salesDocument: { id } }))
    }
  },
  giftCard: {
    schema: createSelector(
      () => null,
      () => [{ "type": "text", "pieces": [{ "text": "GIFT CARD" }] }, { "type": "text", "align": "left", "pieces": [{ "text": "Code: ", "meta": { "bold": false, "underline": false } }, { "text": "", "meta": { "bold": false, "underline": false } }, { "text": ["giftCard", "code"], "meta": { "bold": false, "underline": false } }] }, { "type": "text", "align": "left", "pieces": [{ "text": "Gift card name: ", "meta": { "bold": false, "underline": false } }, { "text": ["product", "name"], "meta": { "bold": false, "underline": false } }] }, { "type": "text", "align": "left", "pieces": [{ "text": "Balance: ", "meta": { "bold": false, "underline": false } }, { "text": ["giftCard", "balance"], "meta": { "bold": false, "underline": false } }] }, { "type": "text", "align": "left", "pieces": [{ "text": "Expires: ", "meta": { "bold": false, "underline": false } }, { "text": ["giftCard", "expirationDate"], "meta": { "bold": false, "underline": false } }] }, { "type": "text", "align": "left", "pieces": [{ "text": "Redeemed by: ", "meta": { "bold": false, "underline": false } }, { "text": ["customer", "fullName"], "meta": { "bold": false, "underline": false } }] }, { "type": "text", "align": "left", "pieces": [] }, { "format": "Code39FullASCII", "type": "barcode", "data": ["giftCard", "giftCardID"] }]
    ),
    prepareData: async (dispatch: ThunkDispatch<unknown, unknown, Action>) => {
      const [{ code }] = await getGiftCard({
        recordsPerPage: 1,
      })
      const { products: [product] } = await dispatch(
        getProductsUniversal({
          recordsOnPage: 1,
        })
      )
      return dispatch(prepareGiftCardData(product.productID, code))
    }
  },
  coupon: {
    schema: createSelector(
      () => null,
      () => [{ "columns": [{ "baseWidth": 0, "weight": 1 }, { "baseWidth": 0, "weight": 1 }], "rows": [{ "cells": [{ "align": "center", "pieces": [{ "meta": { "bold": true, "underline": false }, "text": "COUPON" }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["coupon", "name"] }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["issuedTimestamp"] }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["employee", "employeeName"] }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "address"] }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "phone"] }] }], "type": "normal" }, { "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["uniqueIdentifier"] }] }], "type": "normal" }], "type": "table" }]
    ),
    prepareData: async (dispatch, getState) => {
      const state = getState()
      const { status: { recordsTotal } } = await requestCompleteResponse({
        request: 'getIssuedCoupons',
        recordsOnPage: 1,
      })
      const issuedCoupons = await getIssuedCoupons({
        recordsOnPage: 1,
        pageNo: recordsTotal
      })
      if (!issuedCoupons[0]) {
        const employeeID = getLoggedInEmployeeID(state)
        const { fullName: employeeName } = getEmployees(state)[employeeID]
        const { name: warehouseName } = getSelectedWarehouse(state)
        const { address, phone } = getCompany(state)
        return {
          issuedTimestamp: moment(Date.now()).format(
            'DD/MM/YYYY hh:mm:ss',
          ),
          employeeName,
          warehouseName,
          name: "coupon name",
          address,
          phone,
          couponCode: "coupon code",
          uniqueIdentifier: "19299139133",
          warehouseID: getSelectedWarehouseID(state)
        };
      }
      const [{ couponID }] = await getCoupons({
        recordsOnPage: 1
      })
      const { uniqueIdentifier } = issuedCoupons[0]
      return dispatch(prepareCouponData({ uniqueIdentifier, couponID }))
    }
  },
  defaultType: {
    schema: createSelector(
      () => null,
      (state) => [{ type: 'text', pieces: [{ text: 'TODO: Implement default format' }] }]
    ),
    prepareData: async (dispatch, getState) => {
      return {}
    }
  },
  // these 2 shall be replaced at some point to enable the user to enter schemas for his own type
  kitchen: {
    schema: createSelector(
      () => null,
      () => [{ "align": "center", "pieces": [{ "meta": { "bold": true, "underline": false }, "text": ["translations", "defaults", "kitchen"] }], "type": "text" }, { "align": "center", "pieces": [], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "outer", "translations", "sale", "client"] }, { "meta": { "bold": false, "underline": false }, "text": ": " }, { "meta": { "bold": false, "underline": false }, "text": ["customerFullName"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "outer", "translations", "sale", "saler"] }, { "meta": { "bold": false, "underline": false }, "text": ": " }, { "meta": { "bold": false, "underline": false }, "text": ["employeeFullName"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["date"] }, { "meta": { "bold": false, "underline": false }, "text": ", " }, { "meta": { "bold": false, "underline": false }, "text": ["time"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": true }, "text": ["translations", "sale", "documentNumber"] }, { "meta": { "bold": false, "underline": true }, "text": ": " }, { "meta": { "bold": false, "underline": true }, "text": ["salesDocumentNumber"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": " " }], "type": "text" }, { "columns": [{ "baseWidth": 0, "weight": 3 }, { "baseWidth": 0, "weight": 1 }], "rows": [{ "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["translations", "product", "description"] }] }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["company", "outer", "translations", "product", "quantity"] }] }], "type": "header" }, { "repeat": ["billTable", "billRows"], "sub": [{ "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["product", "nameWithTranslation"] }] }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["amount"] }] }], "type": "normal" }, { "type": "normal", "cells": [{ "align": "left", "pieces": [{ "text": ["product", "notes"], "meta": { "bold": false, "underline": false, "size": -1 } }] }] }] }], "type": "table" }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": " " }], "type": "text" }, { "align": "left", "pieces": [], "type": "text" }],
    ),
    prepareData: async (dispatch, getState) => {
      const [{ id }] = await getSalesDocuments({
        recordsPerPage: 1,
      })
      return dispatch(prepareReceiptData({ salesDocument: { id }, type: "kitchen" }))
    }
  },
  bar: {
    schema: createSelector(
      () => null,
      () => [{ "align": "center", "pieces": [{ "meta": { "bold": true, "underline": false }, "text": ["translations", "defaults", "bar"] }], "type": "text" }, { "align": "center", "pieces": [], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "outer", "translations", "sale", "client"] }, { "meta": { "bold": false, "underline": false }, "text": ": " }, { "meta": { "bold": false, "underline": false }, "text": ["customerFullName"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["company", "outer", "translations", "sale", "saler"] }, { "meta": { "bold": false, "underline": false }, "text": ": " }, { "meta": { "bold": false, "underline": false }, "text": ["employeeFullName"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": false }, "text": ["date"] }, { "meta": { "bold": false, "underline": false }, "text": ", " }, { "meta": { "bold": false, "underline": false }, "text": ["time"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "underline": true }, "text": ["translations", "sale", "documentNumber"] }, { "meta": { "bold": false, "underline": true }, "text": ": " }, { "meta": { "bold": false, "underline": true }, "text": ["salesDocumentNumber"] }], "type": "text" }, { "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": " " }], "type": "text" }, { "columns": [{ "baseWidth": 0, "weight": 3 }, { "baseWidth": 0, "weight": 1 }], "rows": [{ "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["translations", "product", "description"] }] }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["company", "outer", "translations", "product", "quantity"] }] }], "type": "header" }, { "repeat": ["billTable", "billRows"], "sub": [{ "cells": [{ "align": "left", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["product", "nameWithTranslation"] }] }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": ["amount"] }] }], "type": "normal" }, { "type": "normal", "cells": [{ "align": "left", "pieces": [{ "text": ["product", "notes"], "meta": { "bold": false, "underline": false, "size": -1 } }] }] }] }], "type": "table" }, { "align": "center", "pieces": [{ "meta": { "bold": false, "size": 1, "underline": false }, "text": " " }], "type": "text" }, { "align": "left", "pieces": [], "type": "text" }],
    ),
    prepareData: async (dispatch, getState) => {
      const [{ id }] = await getSalesDocuments({
        recordsPerPage: 1,
      })
      return dispatch(prepareReceiptData({ salesDocument: { id }, type: "bar" }))
    }
  }
}

const receiptTypes = INTEGRATIONS[INTEGRATION_TYPES.patchScriptTemplate];
export type PrintoutType = typeof receiptTypes[keyof typeof receiptTypes];

/**
 * Given a printout type, returns whether there is a PatchScript template for that type of printout that is currently stored in CAFA
 * If true, this means updates to the default schemas will not affect this customer anymore
 */
export const hasTemplateForType = (type: PrintoutType) => state =>
  !!getCafaEntry(type, INTEGRATION_TYPES.patchScriptTemplate)(state);

export const getDefaultTemplateForType = (type: PrintoutType) => state =>
  defaults[type]?.schema(state) ?? defaults.defaultType?.schema(state);
/**
 * Given a printout type, returns the PatchScript template for that type of printout that is currently stored in CAFA
 */
export const getTemplateForType = (type: PrintoutType) => state =>
  getCafaEntry2<TItem[]>(
    type,
    INTEGRATION_TYPES.patchScriptTemplate,
    getDefaultTemplateForType(type)(state),
  )(state);

/**
 * Given a printout type, returns a sample data that this printout could have applied to it (f.ex. salesDocument data for receipts, coupon data for coupons, etc.
 * This sample data can be used to render a preview of the format and test whether it is correct
 * Additionally this sample data can be used to populate dropdown fields in the editor
 */
export const sampleDataForType = (type: PrintoutType) => async (
  dispatch,
  getState,
) => dispatch((defaults[type] ?? defaults.defaultType)?.prepareData);

/*
 * 5 => 2.5 => 2 * 3 => 6
 * */
const formatters = {
  'number.round.ceil': precision => value =>
    Math.ceil(Number(value) * precision) / precision,
  'number.round.floor': precision => value =>
    Math.floor(Number(value) * precision) / precision,
  'number.round.higher': precision => value =>
    Math.round(Number(value) * precision) / precision,
  'number.round.zero': precision => value => Number(value).toFixed(precision),
};
